import { Group, Monitor } from "../model";
import { MultiSelectState } from "../ui";
import { GroupSelector } from "./GroupSelector";

// Immutable class
export class GroupSelectors {

  private readonly selectors: GroupSelector[];
  public readonly multiSelectState: MultiSelectState;

  public readonly hasSelectedForDelete: boolean;

  constructor(groups: Group[] | GroupSelector[]) {
    if (groups.length > 0) {
      if ((groups[0] as Group).uuid) {
        this.selectors = (groups as Group[]).map(g => new GroupSelector(g));
      } else {
        this.selectors = (groups as GroupSelector[]);
      }
    } else {
      this.selectors = [];
    }

    let hasSelected = false;
    let hasUnSelected = false;

    let hasSelectedForDelete = false;
    
    this.selectors.forEach(ms => {
      hasSelected = hasSelected || ms.selected;
      hasUnSelected = hasUnSelected || !ms.selected;

      hasSelectedForDelete = hasSelectedForDelete || (ms.canDelete);
    });

    this.multiSelectState =
      hasUnSelected && hasSelected ?
        MultiSelectState.Partially :
      !hasUnSelected && hasSelected ? 
        MultiSelectState.All :
        MultiSelectState.None;

    this.hasSelectedForDelete = hasSelectedForDelete;
  }

  get length(): number {
    return this.selectors ? this.selectors.length : 0;
  }

  get list(): GroupSelector[] {
    return this.selectors;
  }

  delete(groupSelector: GroupSelector): GroupSelectors {
    return new GroupSelectors(this.selectors.map(
      ms => ms.group.uuid === groupSelector.group.uuid ? ms.delete() : ms));
  }

  remove(uuids: string | string[]): GroupSelectors {
    if (!(uuids instanceof Array)) {
      uuids = [uuids];
    }
    return new GroupSelectors(this.selectors.filter(ms => !uuids.includes(ms.group.uuid)));
  }

  select(groupSelector: GroupSelector): GroupSelectors {
    return new GroupSelectors(this.selectors.map(
      ms => ms.group.uuid === groupSelector.group.uuid && !ms.blocked ? ms.select() : ms));
  }
  
  unselect(groupSelector: GroupSelector): GroupSelectors {
    return new GroupSelectors(this.selectors.map(
      ms => ms.group.uuid === groupSelector.group.uuid ? ms.unselect() : ms));
  }

  toggle(groupSelector: GroupSelector): GroupSelectors {
    return groupSelector.selected ? this.unselect(groupSelector) : this.select(groupSelector);
  }

  expand(groupSelector: GroupSelector): GroupSelectors {
    return new GroupSelectors(this.selectors.map(
      ms => ms.group.uuid === groupSelector.group.uuid ? ms.expand() : ms));
  }

  changeMultiSelectState(): GroupSelectors {
    const state = this.multiSelectState !== MultiSelectState.None ?
      MultiSelectState.None :
      MultiSelectState.All;

    return state === MultiSelectState.All ? this.selectAll() : this.unselectAll();
  }

  unselectAll(): GroupSelectors {
    return new GroupSelectors(this.selectors.map(ms => ms.selected ? ms.unselect() : ms));
  }

  selectAll(): GroupSelectors {
    return new GroupSelectors(this.selectors.map(ms => !ms.selected && !ms.blocked ? ms.select() : ms));
  }

  updateMonitorsFromRemote(monitors: Monitor | Monitor[]): GroupSelectors {
    return new GroupSelectors(this.selectors.map(gs => gs.updateMonitorsFromRemote(monitors)));
  }

  updateFromRemote(groups: Group | Group[]): GroupSelectors {
    if (!(groups instanceof Array)) {
      groups = [groups];
    }
    return new GroupSelectors(this.selectors.map(
      gs => {
        const g = (groups as Group[]).find(g => g.uuid === gs.group.uuid);
        return g ? gs.updateFromRemote(g) : gs;
      }));
  }
}